import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  runTransaction,
  arrayUnion,
  deleteField
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../../firebase/config';
import { accountErrorMessage } from '../../utils/accountErrors';
import { deleteAllEntityDocumentFiles } from '../../services/entityDocumentsService';
import { getMaintenanceType } from '../../config/vehicleMaintenance';
import { isCentralRole, isOperatorAdminRole, isOperatorRole } from './authSlice';


const getCurrentUser = (state) => state.auth?.user || null;

const getOperatorScope = (state) => {
  const currentUser = getCurrentUser(state);
  return isOperatorRole(currentUser) ? currentUser.operatorId || null : null;
};

const assertOperatorAccess = (currentUser, data, entityName = 'Datensatz') => {
  if (isCentralRole(currentUser)) return;
  if (!isOperatorRole(currentUser) || !currentUser.operatorId) {
    throw new Error('Keine Berechtigung');
  }
  if (data.operatorId !== currentUser.operatorId) {
    throw new Error(`${entityName} gehoert zu einem anderen Unternehmer`);
  }
};

const assertCanManageVehicles = (currentUser) => {
  if (isCentralRole(currentUser) || isOperatorAdminRole(currentUser)) return;
  throw new Error('Nur zentrale Rollen und Unternehmer-Admins duerfen Fahrzeuge verwalten');
};

const toIsoOrNull = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  if (value.seconds != null) return new Date(value.seconds * 1000).toISOString();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

// Wartungseinträge: Altbestand trägt `date` als Firestore-Timestamp und nur
// eine freie Beschreibung. Für Redux (serialisierbar) und die Anzeige auf
// ISO-Strings bringen.
const normalizeMaintenanceHistory = (history) => (
  (Array.isArray(history) ? history : []).map(record => ({
    ...record,
    date: toIsoOrNull(record?.date),
    nextDueDate: toIsoOrNull(record?.nextDueDate),
    createdAt: toIsoOrNull(record?.createdAt)
  }))
);

// Fälligkeiten je Wartungsart (`Vehicles.maintenanceDue`, siehe
// config/vehicleMaintenance.js) — nur bekannte, fristgebundene Schlüssel.
const normalizeMaintenanceDue = (due) => {
  const result = {};
  if (!due || typeof due !== 'object') return result;
  Object.entries(due).forEach(([key, value]) => {
    const iso = toIsoOrNull(value);
    if (iso && getMaintenanceType(key)?.tracksDue) result[key] = iso;
  });
  return result;
};

const loadVehicleDriver = async (driverId) => {
  if (!driverId) return null;
  const userDoc = await getDoc(doc(db, 'users', driverId));
  if (!userDoc.exists() || !userDoc.data().isDriver) return null;
  return {
    id: driverId,
    ...userDoc.data(),
    user: {
      name: userDoc.data().name,
      email: userDoc.data().email,
      phone: userDoc.data().phone || '',
      profile_picture: userDoc.data().profilePic || ''
    }
  };
};

// Einheitliche Abbildung eines Vehicles-Dokuments. Bisher baute jeder Thunk
// sein eigenes Teilobjekt — nach „Fahrzeug freigeben" fehlten auf der
// Detailseite deshalb Telefon, Zahlarten und Dokumente.
const mapVehicleDoc = (id, vehicleData, driverData) => {
  // UI-Status: Reservefahrzeug (active === false) → 'inactive', sonst nach inUse
  const uiStatus = vehicleData.active === false ? 'inactive' : vehicleData.inUse ? 'in_use' : 'active';
  return {
    id,
    vehicleNumber: vehicleData.vehicleNumber || '',
    model: vehicleData.model,
    licensePlate: vehicleData.licensePlate,
    phoneNumber: vehicleData.phoneNumber || '',
    ordnungsnummer: vehicleData.ordnungsnummer || '',
    inUse: vehicleData.inUse || false,
    status: uiStatus,
    active: vehicleData.active !== false,
    lastActivity: vehicleData.lastActivityTimestamp || null,
    vehicleType: vehicleData.vehicleType || 1,
    specialCapabilities: vehicleData.specialCapabilities || (vehicleData.specialVehicleType ? [vehicleData.specialVehicleType] : []),
    paymentMethods: vehicleData.paymentMethods || 1,
    taximeterCoupling: vehicleData.taximeterCoupling || 'none',
    organizationId: vehicleData.organizationId || null,
    operatorId: vehicleData.operatorId || null,
    inspectionDate: vehicleData.inspectionDate || null,
    tuvDate: vehicleData.tuvDate || null,
    maintenanceDue: normalizeMaintenanceDue(vehicleData.maintenanceDue),
    documents: vehicleData.documents || [],
    current_driver: driverData,
    year: vehicleData.year,
    capacity: vehicleData.capacity || 0,
    features: Array.isArray(vehicleData.features) ? vehicleData.features : [],
    maintenanceHistory: normalizeMaintenanceHistory(vehicleData.maintenanceHistory),
    totalDistance: vehicleData.totalDistance || 0,
    createdAt: toIsoOrNull(vehicleData.created_at)
  };
};

// Async thunks
export const fetchVehicles = createAsyncThunk(
  'vehicles/fetchVehicles',
  async (_, { getState, rejectWithValue }) => {
    try {
      const operatorScope = getOperatorScope(getState());
      const vehiclesQuery = operatorScope
        ? query(
            collection(db, 'Vehicles'),
            where('operatorId', '==', operatorScope),
            orderBy('model', 'asc')
          )
        : query(
            collection(db, 'Vehicles'),
            orderBy('model', 'asc')
          );
      
      const querySnapshot = await getDocs(vehiclesQuery);
      const vehicles = [];
      
      for (const vehicleDoc of querySnapshot.docs) {
        const vehicleData = vehicleDoc.data();
        const vehicleId = vehicleDoc.id;
        
        const driverData = await loadVehicleDriver(vehicleData.driverId);
        vehicles.push(mapVehicleDoc(vehicleId, vehicleData, driverData));
      }

      return vehicles;
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchVehicleById = createAsyncThunk(
  'vehicles/fetchVehicleById',
  async (vehicleId, { getState, rejectWithValue }) => {
    try {
      const vehicleDoc = await getDoc(doc(db, 'Vehicles', vehicleId));
      
      if (!vehicleDoc.exists()) {
        throw new Error('Fahrzeug nicht gefunden');
      }
      
      const vehicleData = vehicleDoc.data();
      const currentUser = getCurrentUser(getState());
      assertOperatorAccess(currentUser, vehicleData, 'Fahrzeug');

      const driverData = await loadVehicleDriver(vehicleData.driverId);
      return mapVehicleDoc(vehicleId, vehicleData, driverData);
    } catch (error) {
      console.error('Error fetching vehicle by ID:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fahrzeug anlegen — über die CF `createVehicleWithQuota`.
 *
 * Die Kontingentprüfung lief bisher hier im Thunk und war damit umgehbar; die
 * Firestore-Rules erlaubten `create` auf `Vehicles` sogar jedem Fahrer. Beides
 * ist geschlossen: Die Rules sperren Client-Schreiber, und die CF zählt den
 * Bestand transaktional gegen das Kontingent des Unternehmens bzw. gegen den
 * Lizenzrahmen. Die Rolle des Aufrufers und die operatorId prüft ebenfalls die
 * CF — ein Unternehmer-Konto legt immer nur für das eigene Unternehmen an.
 */
export const createVehicle = createAsyncThunk(
  'vehicles/createVehicle',
  async (vehicleData, { rejectWithValue, getState }) => {
    try {
      const currentUser = getCurrentUser(getState());
      assertCanManageVehicles(currentUser);

      const createVehicleFn = httpsCallable(functions, 'createVehicleWithQuota');
      const result = await createVehicleFn({ vehicle: vehicleData });

      return { id: result.data?.id, ...(result.data?.vehicle || {}) };
    } catch (error) {
      console.error('Error creating vehicle:', error);
      return rejectWithValue(accountErrorMessage(error, 'Das Fahrzeug konnte nicht angelegt werden.'));
    }
  }
);

/**
 * Fahrzeug als Reserve deaktivieren bzw. wieder aktivieren — über die CF
 * `setVehicleActive`. Deaktivierte Fahrzeuge zählen nicht gegen Kontingent/
 * Lizenzrahmen; beim Aktivieren prüft die CF das Kontingent erneut. Die Rules
 * sperren das Feld `active` für Client-Schreiber.
 */
export const setVehicleActive = createAsyncThunk(
  'vehicles/setVehicleActive',
  async ({ id, active }, { rejectWithValue, getState }) => {
    try {
      const currentUser = getCurrentUser(getState());
      assertCanManageVehicles(currentUser);

      const setVehicleActiveFn = httpsCallable(functions, 'setVehicleActive');
      await setVehicleActiveFn({ vehicleId: id, active: active === true });
      return { id, active: active === true };
    } catch (error) {
      console.error('Error toggling vehicle active state:', error);
      return rejectWithValue(accountErrorMessage(
        error,
        active ? 'Das Fahrzeug konnte nicht aktiviert werden.' : 'Das Fahrzeug konnte nicht deaktiviert werden.'
      ));
    }
  }
);

export const updateVehicle = createAsyncThunk(
  'vehicles/updateVehicle',
  async ({ id, ...vehicleData }, { getState, rejectWithValue }) => {
    try {
      const vehicleRef = doc(db, 'Vehicles', id);
      const vehicleDoc = await getDoc(vehicleRef);
      
      if (!vehicleDoc.exists()) {
        throw new Error('Fahrzeug nicht gefunden');
      }

      const currentUser = getCurrentUser(getState());
      assertCanManageVehicles(currentUser);
      assertOperatorAccess(currentUser, vehicleDoc.data(), 'Fahrzeug');
      
      // Update-Daten vorbereiten
      const updateData = {};

      if (vehicleData.vehicleNumber !== undefined) {
        const normalizedVehicleNumber = (vehicleData.vehicleNumber || '').trim();
        if (normalizedVehicleNumber) {
          const numberSnapshot = await getDocs(query(
            collection(db, 'Vehicles'),
            where('vehicleNumber', '==', normalizedVehicleNumber)
          ));
          const duplicate = numberSnapshot.docs.find(docSnapshot => docSnapshot.id !== id);
          if (duplicate) {
            throw new Error(`Fahrzeugnummer ${normalizedVehicleNumber} ist bereits vergeben`);
          }
        }
        updateData.vehicleNumber = normalizedVehicleNumber;
      }
      if (vehicleData.model) updateData.model = vehicleData.model;
      if (vehicleData.licensePlate) updateData.licensePlate = vehicleData.licensePlate;
      if (vehicleData.phoneNumber !== undefined) updateData.phoneNumber = (vehicleData.phoneNumber || '').trim();
      if (vehicleData.ordnungsnummer !== undefined) {
        updateData.ordnungsnummer = (vehicleData.ordnungsnummer || '').trim().slice(0, 40) || null;
      }
      if (vehicleData.year) updateData.year = vehicleData.year;
      if (vehicleData.capacity) updateData.capacity = vehicleData.capacity;
      if (vehicleData.features) updateData.features = vehicleData.features;
      if (vehicleData.vehicleType !== undefined) updateData.vehicleType = vehicleData.vehicleType;
      if (vehicleData.specialCapabilities !== undefined) updateData.specialCapabilities = vehicleData.specialCapabilities;
      if (vehicleData.paymentMethods !== undefined) updateData.paymentMethods = vehicleData.paymentMethods;
      if (vehicleData.status !== undefined) updateData.status = vehicleData.status;
      if (vehicleData.inspectionDate !== undefined) updateData.inspectionDate = vehicleData.inspectionDate;
      if (vehicleData.tuvDate !== undefined) updateData.tuvDate = vehicleData.tuvDate;
      if (vehicleData.maintenanceDue !== undefined) {
        const due = normalizeMaintenanceDue(vehicleData.maintenanceDue);
        updateData.maintenanceDue = due;
        // `tuvDate` („TÜV bis") bleibt als Spiegel für ältere Leser erhalten.
        updateData.tuvDate = due.tuv || null;
      }
      if (vehicleData.documents !== undefined) updateData.documents = vehicleData.documents;
      
      // Wenn ein Fahrer zugewiesen werden soll
      if (vehicleData.driverId) {
        const driverDoc = await getDoc(doc(db, 'users', vehicleData.driverId));
        if (!driverDoc.exists() || !driverDoc.data().isDriver) {
          throw new Error('Fahrer nicht gefunden');
        }
        if ((vehicleDoc.data().operatorId || null) !== (driverDoc.data().operatorId || null)) {
          throw new Error('Fahrer und Fahrzeug muessen zum selben Unternehmer gehoeren');
        }
        updateData.driverId = vehicleData.driverId;
        updateData.inUse = true;
      }
      
      // Wenn der Fahrer entfernt werden soll
      if (vehicleData.driverId === null) {
        updateData.driverId = null;
        updateData.inUse = false;
      }
      
      // Fahrzeugdaten aktualisieren
      await updateDoc(vehicleRef, {
        ...updateData,
        lastActivityTimestamp: Timestamp.now()
      });
      
      // Aktualisierte Daten direkt vom Server abrufen
      const updatedVehicleDoc = await getDoc(vehicleRef);
      const updatedVehicleData = updatedVehicleDoc.data();
      
      const driverData = await loadVehicleDriver(updatedVehicleData.driverId);
      return mapVehicleDoc(id, updatedVehicleData, driverData);
    } catch (error) {
      console.error('Error updating vehicle:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteVehicle = createAsyncThunk(
  'vehicles/deleteVehicle',
  async (id, { getState, rejectWithValue }) => {
    try {
      const vehicleRef = doc(db, 'Vehicles', id);
      const vehicleDoc = await getDoc(vehicleRef);
      
      if (!vehicleDoc.exists()) {
        throw new Error('Fahrzeug nicht gefunden');
      }
      
      const vehicleData = vehicleDoc.data();
      const currentUser = getCurrentUser(getState());
      assertCanManageVehicles(currentUser);
      assertOperatorAccess(currentUser, vehicleData, 'Fahrzeug');
      
      // Prüfen, ob Fahrzeug aktuell in Benutzung ist
      if (vehicleData.inUse && vehicleData.driverId) {
        throw new Error('Fahrzeug ist aktuell einem Fahrer zugewiesen und kann nicht gelöscht werden');
      }
      
      // Dokument-Dateien zuerst entfernen: Die Storage-Rules prüfen die
      // Berechtigung am Fahrzeugdokument — ist es weg, ließen sich die Dateien
      // aus dem Portal nicht mehr löschen.
      await deleteAllEntityDocumentFiles(vehicleData.documents);

      // Fahrzeug löschen
      await deleteDoc(vehicleRef);
      
      return id;
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const releaseVehicle = createAsyncThunk(
  'vehicles/releaseVehicle',
  async (id, { getState, rejectWithValue }) => {
    try {
      const vehicleRef = doc(db, 'Vehicles', id);
      const vehicleDoc = await getDoc(vehicleRef);
      
      if (!vehicleDoc.exists()) {
        throw new Error('Fahrzeug nicht gefunden');
      }
      
      const vehicleData = vehicleDoc.data();
      const currentUser = getCurrentUser(getState());
      assertCanManageVehicles(currentUser);
      assertOperatorAccess(currentUser, vehicleData, 'Fahrzeug');
      
      // Prüfen, ob Fahrzeug einem Fahrer zugewiesen ist
      if (!vehicleData.inUse || !vehicleData.driverId) {
        throw new Error('Fahrzeug ist keinem Fahrer zugewiesen');
      }

      // Fahrer-Referenz holen
      const driverId = vehicleData.driverId;
      const driverRef = doc(db, 'users', driverId);
      const driverDoc = await getDoc(driverRef);
      
      if (!driverDoc.exists()) {
        throw new Error('Fahrer nicht gefunden');
      }
      
      // Remote-Logout für den Fahrer aktivieren
      await updateDoc(driverRef, {
        car: null,
        forceLogout: true,
        forceLogoutTimestamp: Timestamp.now(),
        lastLogoutCheck: null,
        isOnline: false,
        isAvailable: false,
      });

      // driverLocations ebenfalls aktualisieren (für Dispatcher-Anzeige und Wartezeitberechnung)
      try {
        const driverLocRef = doc(db, 'driverLocations', driverId);
        const driverLocSnap = await getDoc(driverLocRef);
        if (driverLocSnap.exists()) {
          await updateDoc(driverLocRef, { isAvailable: false });
        }
      } catch (locErr) {
        console.warn('driverLocations Update fehlgeschlagen:', locErr);
      }
      
      // Batch-Update für Fahrzeug
      await updateDoc(vehicleRef, {
        inUse: false,
        driverId: null,
        lastActivityTimestamp: Timestamp.now()
      });
      
      // Aktualisierte Fahrzeugdaten zurückgeben
      const updatedVehicleDoc = await getDoc(vehicleRef);
      const updatedVehicleData = updatedVehicleDoc.data();
      
      return mapVehicleDoc(id, updatedVehicleData, null);
    } catch (error) {
      console.error('Error releasing vehicle:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Wartung eintragen (TÜV, Inspektion, Eichung, Ölwechsel, Sonstiges).
 *
 * Bei fristgebundenen Arten setzt `nextDueDate` die Fälligkeit am Fahrzeug
 * (`maintenanceDue.<art>`) — aber nur, wenn sie später liegt als die bisher
 * hinterlegte: Ein nachgetragener älterer Termin soll eine aktuelle Frist nicht
 * zurückdrehen. Korrigieren lässt sich die Frist im Bearbeiten-Formular.
 */
export const addMaintenanceRecord = createAsyncThunk(
  'vehicles/addMaintenanceRecord',
  async ({ vehicleId, record }, { getState, rejectWithValue, dispatch }) => {
    try {
      const currentUser = getCurrentUser(getState());
      assertCanManageVehicles(currentUser);

      const type = getMaintenanceType(record.type);
      if (!type) throw new Error('Unbekannte Wartungsart');
      const date = toIsoOrNull(record.date) || new Date().toISOString();
      const nextDueDate = type.tracksDue ? toIsoOrNull(record.nextDueDate) : null;

      const vehicleRef = doc(db, 'Vehicles', vehicleId);
      await runTransaction(db, async (transaction) => {
        const vehicleDoc = await transaction.get(vehicleRef);
        if (!vehicleDoc.exists()) throw new Error('Fahrzeug nicht gefunden');
        const vehicleData = vehicleDoc.data();
        assertOperatorAccess(currentUser, vehicleData, 'Fahrzeug');

        const maintenanceRecord = {
          id: `${Date.now()}`,
          type: type.key,
          description: (record.description || '').trim(),
          date,
          nextDueDate,
          odometer: Number.isFinite(record.odometer) ? record.odometer : null,
          cost: Number.isFinite(record.cost) ? record.cost : null,
          notes: (record.notes || '').trim(),
          createdAt: new Date().toISOString(),
          createdBy: auth.currentUser?.uid || null
        };

        const update = {
          maintenanceHistory: arrayUnion(maintenanceRecord),
          lastMaintenance: Timestamp.now(),
          lastActivityTimestamp: Timestamp.now()
        };

        if (nextDueDate) {
          const currentDue = normalizeMaintenanceDue(vehicleData.maintenanceDue)[type.key]
            || (type.key === 'tuv' ? toIsoOrNull(vehicleData.tuvDate) : null);
          if (!currentDue || new Date(nextDueDate) > new Date(currentDue)) {
            update[`maintenanceDue.${type.key}`] = nextDueDate;
            if (type.key === 'tuv') update.tuvDate = nextDueDate;
          }
        }

        transaction.update(vehicleRef, update);
      });

      const result = await dispatch(fetchVehicleById(vehicleId));
      return result.payload;
    } catch (error) {
      console.error('Error adding maintenance record:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Wartungseintrag löschen (z. B. Tippfehler). Stammte die aktuelle Frist aus
 * diesem Eintrag, wird sie aus den verbleibenden Einträgen derselben Art neu
 * bestimmt bzw. entfernt.
 */
export const deleteMaintenanceRecord = createAsyncThunk(
  'vehicles/deleteMaintenanceRecord',
  async ({ vehicleId, recordId }, { getState, rejectWithValue, dispatch }) => {
    try {
      const currentUser = getCurrentUser(getState());
      assertCanManageVehicles(currentUser);

      const vehicleRef = doc(db, 'Vehicles', vehicleId);
      await runTransaction(db, async (transaction) => {
        const vehicleDoc = await transaction.get(vehicleRef);
        if (!vehicleDoc.exists()) throw new Error('Fahrzeug nicht gefunden');
        const vehicleData = vehicleDoc.data();
        assertOperatorAccess(currentUser, vehicleData, 'Fahrzeug');

        const history = Array.isArray(vehicleData.maintenanceHistory) ? vehicleData.maintenanceHistory : [];
        const removed = history.find(record => String(record?.id) === String(recordId));
        if (!removed) throw new Error('Wartungseintrag nicht gefunden');
        const remaining = history.filter(record => record !== removed);

        const update = {
          maintenanceHistory: remaining,
          lastActivityTimestamp: Timestamp.now()
        };

        const type = getMaintenanceType(removed.type);
        const removedDue = toIsoOrNull(removed.nextDueDate);
        const currentDue = type?.tracksDue ? normalizeMaintenanceDue(vehicleData.maintenanceDue)[type.key] : null;
        if (type?.tracksDue && removedDue && currentDue === removedDue) {
          const fallback = remaining
            .filter(record => record?.type === type.key)
            .map(record => toIsoOrNull(record.nextDueDate))
            .filter(Boolean)
            .sort()
            .pop() || null;
          update[`maintenanceDue.${type.key}`] = fallback || deleteField();
          if (type.key === 'tuv') update.tuvDate = fallback;
        }

        transaction.update(vehicleRef, update);
      });

      const result = await dispatch(fetchVehicleById(vehicleId));
      return result.payload;
    } catch (error) {
      console.error('Error deleting maintenance record:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Aktiver Listener-Speicher außerhalb des Reducer, um Cleanup zu ermöglichen
let vehiclesStatusListener = null;

const initialState = {
  vehicles: [],
  currentVehicle: null,
  isLoading: false,
  error: null
};

const vehiclesSlice = createSlice({
  name: 'vehicles',
  initialState,
  reducers: {
    clearVehiclesError: (state) => {
      state.error = null;
    },
    clearCurrentVehicle: (state) => {
      state.currentVehicle = null;
    },
    updateVehicleRealtime: (state, action) => {
      const vehicleData = action.payload;
      const index = state.vehicles.findIndex(vehicle => vehicle.id === vehicleData.id);
      
      if (index !== -1) {
        // Nur die aktualisierten Felder übernehmen
        state.vehicles[index] = {
          ...state.vehicles[index],
          ...vehicleData
        };
      }
      
      // Auch das aktuelle Fahrzeug aktualisieren, falls es dasselbe ist
      if (state.currentVehicle && state.currentVehicle.id === vehicleData.id) {
        state.currentVehicle = {
          ...state.currentVehicle,
          ...vehicleData
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Reserve (de)aktivieren — Fehler meldet die aufrufende Komponente
      // selbst (unwrap), damit keine Seite in den Fehlerzustand kippt.
      .addCase(setVehicleActive.fulfilled, (state, action) => {
        const { id, active } = action.payload;
        const apply = (vehicle) => {
          vehicle.active = active;
          vehicle.status = !active ? 'inactive' : vehicle.inUse ? 'in_use' : 'active';
        };
        const vehicle = state.vehicles.find(v => v.id === id);
        if (vehicle) apply(vehicle);
        if (state.currentVehicle?.id === id) apply(state.currentVehicle);
      })
      // Fetch all vehicles
      .addCase(fetchVehicles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vehicles = action.payload;
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch vehicle by ID
      .addCase(fetchVehicleById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVehicleById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentVehicle = action.payload;
      })
      .addCase(fetchVehicleById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create vehicle
      .addCase(createVehicle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vehicles.push(action.payload);
        state.currentVehicle = action.payload;
      })
      .addCase(createVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update vehicle
      .addCase(updateVehicle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.vehicles.findIndex(vehicle => vehicle.id === action.payload.id);
        if (index !== -1) {
          state.vehicles[index] = action.payload;
        }
        if (state.currentVehicle && state.currentVehicle.id === action.payload.id) {
          state.currentVehicle = action.payload;
        }
      })
      .addCase(updateVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete vehicle
      .addCase(deleteVehicle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vehicles = state.vehicles.filter(vehicle => vehicle.id !== action.payload);
        if (state.currentVehicle && state.currentVehicle.id === action.payload) {
          state.currentVehicle = null;
        }
      })
      .addCase(deleteVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add maintenance record
      // Wartung eintragen/löschen: Fehler zeigt der Dialog an. Ein globales
      // `error` würde die ganze Detailseite durch die Fehlermeldung ersetzen.
      .addCase(addMaintenanceRecord.fulfilled, (state, action) => {
        if (!action.payload) return;
        const index = state.vehicles.findIndex(vehicle => vehicle.id === action.payload.id);
        if (index !== -1) {
          state.vehicles[index] = action.payload;
        }
        if (state.currentVehicle && state.currentVehicle.id === action.payload.id) {
          state.currentVehicle = action.payload;
        }
      })
      .addCase(deleteMaintenanceRecord.fulfilled, (state, action) => {
        if (!action.payload) return;
        const index = state.vehicles.findIndex(vehicle => vehicle.id === action.payload.id);
        if (index !== -1) {
          state.vehicles[index] = action.payload;
        }
        if (state.currentVehicle && state.currentVehicle.id === action.payload.id) {
          state.currentVehicle = action.payload;
        }
      })
      // Release vehicle
      .addCase(releaseVehicle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(releaseVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.vehicles.findIndex(vehicle => vehicle.id === action.payload.id);
        if (index !== -1) {
          state.vehicles[index] = action.payload;
        }
        if (state.currentVehicle && state.currentVehicle.id === action.payload.id) {
          state.currentVehicle = action.payload;
        }
      })
      .addCase(releaseVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

// Thunk zum Starten des Echtzeit-Listeners für Fahrzeug-Status
export const startVehiclesStatusListener = () => async (dispatch, getState) => {
  console.log('Starte Echtzeit-Listener für Fahrzeug-Status...');
  
  // Existierenden Listener entfernen, falls vorhanden
  if (vehiclesStatusListener) {
    console.log('Beende alten Fahrzeug-Listener...');
    vehiclesStatusListener();
    vehiclesStatusListener = null;
  }
  
  // Spezielle Anpassungen für Produktionsumgebungen
  const PROD_ENV = process.env.NODE_ENV === 'production';
  
  // Helper für zuverlässigere Echtzeit-Updates in Produktionsumgebungen
  const enhanceProductionReliability = (callback) => {
    return (...args) => {
      // In Produktionsumgebungen zusätzliche Verzögerung für stabilere Verbindungen
      if (PROD_ENV) {
        console.log('VehiclesSlice: Produktionsoptimierungen werden angewendet');
        setTimeout(() => callback(...args), 500);
      } else {
        callback(...args);
      }
    };
  };
  
  // Listener für die Fahrzeugsammlung einrichten
  const operatorScope = getOperatorScope(getState());
  const vehiclesQuery = operatorScope
    ? query(
        collection(db, 'Vehicles'),
        where('operatorId', '==', operatorScope),
        orderBy('model', 'asc')
      )
    : query(
        collection(db, 'Vehicles'),
        orderBy('model', 'asc')
      );
  
  // In Produktionsumgebungen robusteres Error-Handling
  const errorHandler = (error) => {
    console.error('Fehler im Fahrzeug-Status-Listener:', error);
    
    if (PROD_ENV) {
      console.log('VehiclesSlice: Versuche Fahrzeug-Listener neu zu starten...');
      // Im Produktionsmodus nach Fehlern neu starten
      setTimeout(() => {
        try {
          dispatch(startVehiclesStatusListener());
          console.log('VehiclesSlice: Listener erfolgreich neu gestartet');
        } catch (e) {
          console.error('VehiclesSlice: Neustart des Listeners fehlgeschlagen:', e);
        }
      }, 2000);
    }
  };
  
  // Wrapper für zuverlässigere Callbacks in Produktionsumgebungen
  const processVehicleChanges = enhanceProductionReliability(async (snapshot) => {
    for (const change of snapshot.docChanges()) {
        const vehicleData = change.doc.data();
        const vehicleId = change.doc.id;
        
        // Nur bei neuen oder geänderten Dokumenten
        if (change.type === 'added' || change.type === 'modified') {
          console.log(`Echtzeit-Update für Fahrzeug ${vehicleId}:`, vehicleData.inUse, vehicleData.driverId);
          
          // Aktuellen Fahrer abrufen, falls zugewiesen
          let driverData = null;
          if (vehicleData.driverId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', vehicleData.driverId));
              
              if (userDoc.exists() && userDoc.data().isDriver) {
                driverData = {
                  id: vehicleData.driverId,
                  ...userDoc.data(),
                  user: {
                    name: userDoc.data().name,
                    email: userDoc.data().email,
                    phone: userDoc.data().phone || '',
                    profile_picture: userDoc.data().profilePic || ''
                  }
                };
              }
            } catch (error) {
              console.error(`Fehler beim Abrufen des Fahrers für Fahrzeug ${vehicleId}:`, error);
            }
          }
          
          // UI-Status: Reservefahrzeug (active === false) → 'inactive', sonst nach inUse
          const uiStatus = vehicleData.active === false ? 'inactive' : vehicleData.inUse ? 'in_use' : 'active';
          
          // Relevante Statusdaten extrahieren und an den Store senden
          dispatch({
            type: 'vehicles/updateVehicleRealtime',
            payload: {
              id: vehicleId,
              inUse: vehicleData.inUse || false,
              status: uiStatus,
              active: vehicleData.active !== false,
              current_driver: driverData,
              organizationId: vehicleData.organizationId || null,
              operatorId: vehicleData.operatorId || null,
              lastActivity: vehicleData.lastActivityTimestamp || null
            }
          });
        }
      }
    
    if (PROD_ENV) {
      console.log('VehiclesSlice: Fahrzeug-Updates verarbeitet', 
        {timestamp: new Date().toISOString(), changes: snapshot.docChanges().length});
    }
  });
  
  vehiclesStatusListener = onSnapshot(
    vehiclesQuery,
    {
      includeMetadataChanges: PROD_ENV // In Produktion auch Metadaten-Updates beobachten
    },
    processVehicleChanges,
    errorHandler
  );
  
  if (PROD_ENV) {
    console.log('VehiclesSlice: Echtzeit-Listener erfolgreich gestartet (Produktionsmodus)');
  }
  
  return () => {
    // Cleanup-Funktion zurückgeben
    if (vehiclesStatusListener) {
      vehiclesStatusListener();
      vehiclesStatusListener = null;
    }
  };
};

// Thunk zum Stoppen des Echtzeit-Listeners
export const stopVehiclesStatusListener = () => () => {
  console.log('Stoppe Echtzeit-Listener für Fahrzeug-Status...');
  if (vehiclesStatusListener) {
    vehiclesStatusListener();
    vehiclesStatusListener = null;
  }
};

export const { clearVehiclesError, clearCurrentVehicle, updateVehicleRealtime } = vehiclesSlice.actions;
export default vehiclesSlice.reducer;
